import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';

// Lodash
import isNil from 'lodash/isNil';

import {
    DialogBody,
    Heading,
    IconReload,
    Text,
    Textarea,
    FormHelp,
} from '@zillow/constellation';


import ImageCarousel from 'app/shared/components/ImageCarousel';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import {
    StyledImageCarouselModal,
} from './StyledComponents';

const SAVE_CAPTION_DELAY = 200;

class ImageCarouselModal extends React.Component {
    static propTypes = {
        backToOrigPos: PropTypes.bool,
        className: PropTypes.string,
        currentSlideIndex: PropTypes.number,
        isOpen: PropTypes.bool.isRequired,
        isListingPhotoProcessing: PropTypes.bool,
        list: PropTypes.array,
        startSlideIndex: PropTypes.number,
        statusMessage: PropTypes.string,
        // actions
        rotatePhoto: PropTypes.func,
        savePhotoCaption: PropTypes.func,
        updateCaption: PropTypes.func,
        updateCurrentSlideIndex: PropTypes.func,
    };

    static defaultProps = {
        backToOrigPos: true,
        className: 'ImageCarouselModal',
        currentSlideIndex: 0,
        isListingPhotoProcessing: false,
        list: [],
        startSlideIndex: 0,
        statusMessage: '',
        rotatePhoto: () => {},
        savePhotoCaption: () => {},
        updateCaption: () => {},
        updateCurrentSlideIndex: () => {},
    };

    constructor(props) {
        super(props);

        // init state
        this.state = {
            currentSlideIndex: this.props.currentSlideIndex,
            isOpen: this.props.isOpen,
            isListingPhotoProcessing: this.props.isListingPhotoProcessing,
            list: this.props.list,
            origPosY: 0,
            isRotating: false,
            statusMessage: this.props.statusMessage,
        };

        // bind
        this.getCurrentItem = this.getCurrentItem.bind(this);
        this.handleCaptionBlur = this.handleCaptionBlur.bind(this);
        this.handleCaptionUpdate = this.handleCaptionUpdate.bind(this);
        this.handleRotateClick = this.handleRotateClick.bind(this);
        this.modalClose = this.modalClose.bind(this);
        this.renderForm = this.renderForm.bind(this);
        this.savePhotoCaption = this.savePhotoCaption.bind(this);
        this.updateCurrentSlideIndex = this.updateCurrentSlideIndex.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        this.setState({
            currentSlideIndex: nextProps.currentSlideIndex,
            isOpen: nextProps.isOpen,
            isListingPhotoProcessing: nextProps.isListingPhotoProcessing,
            list: nextProps.list,
            startSlide: nextProps.startSlideIndex,
            statusMessage: nextProps.statusMessage,
            origPosY: this.state.isOpen ? this.state.origPosY : window.scrollY, // eslint-disable-line
        });
    }

    getCurrentItem() {
        const { currentSlideIndex, list } = this.state;

        return list[currentSlideIndex];
    }

    handleCaptionBlur() {
        // save current photo so savePhotoCaption doesn't save the caption of the photo the user is on after the delay
        const currentItem = this.getCurrentItem();
        setTimeout(() => {
            // prevent duplicate calls to save photo caption
            // save photo caption is triggered on rotate click as well
            if (!this.state.isListingPhotoProcessing) {
                this.savePhotoCaption(currentItem);
            }
        }, SAVE_CAPTION_DELAY);
    }

    handleCaptionUpdate(e) {
        const { currentSlideIndex, list } = this.state;
        list[currentSlideIndex].caption = e.target.value;

        this.props.updateCaption(e.target.value, this.state.currentSlideIndex);
        this.setState({ list });
    }

    keyDownHandler = handler => (e, index) => {
        // 13 === enter 32 === spacebar
        if (e.keyCode === 13 || e.keyCode === 32) {
            handler(index);
        }
    };

    handleRotateClick() {
        if (!this.props.isDisabled) {
            this.savePhotoCaption().then(() => {
                this.setState({ isRotating: true });
                this.props.rotatePhoto(this.state.currentSlideIndex)
                    .then(() => this.setState({ isRotating: false }))
                    .catch(() => {});
            });
        }
    }

    savePhotoCaption(item = this.getCurrentItem()) {
        if (isNil(item) || this.state.isListingPhotoProcessing) {
            return Promise.resolve();
        }

        return this.props.savePhotoCaption(item.id, item.caption);
    }

    modalClose() {
        const { backToOrigPos, dispatch } = this.props;
        const { origPosY } = this.state;

        dispatch(ModalActions.updateDisplayImageCarouselModal({ isOpen: false }));

        // check to reset position
        if (backToOrigPos) {
            setTimeout(() => {
                window.scroll(0, origPosY);
            }, 0);
        }
    }

    modalOpen() {
        this.props.dispatch(ModalActions.updateDisplayImageCarouselModal({ isOpen: true }));
    }

    updateCurrentSlideIndex(index) {
        this.props.updateCurrentSlideIndex(index);
    }

    renderForm() {
        const { isListingPhotoProcessing, statusMessage } = this.state;
        const { isDisabled } = this.props;

        const item = this.getCurrentItem();
        if (isNil(item)) {
            return false;
        }

        const caption = item.hasOwnProperty('caption') ? item.caption : '';
        const placeholder = 'Describe this photo';
        const errorMsg = '';

        return (
            <div className="ImageCarouselModal-Form">
                <Textarea
                    aria-label="Image Description"
                    marginBottom="xs"
                    className={cx('ImageCarouselModal-Form-Textarea', {
                        disabled: isListingPhotoProcessing,
                    })}
                    error={errorMsg}
                    placeholder={placeholder}
                    value={caption}
                    // actions
                    onBlur={this.handleCaptionBlur}
                    onChange={this.handleCaptionUpdate}
                    disabled={isDisabled}
                />
                {errorMsg &&
                    <FormHelp error={true}>{errorMsg}</FormHelp>
                }

                <div className="ImageCarouselModal-Form-Actions">
                    <Text
                        className={cx(
                            'Status', {
                                'Status-processing': isListingPhotoProcessing,
                            }
                        )}
                        fontType="sectionheading"
                        as="div"
                    >
                        {statusMessage}
                    </Text>

                    <IconReload
                        aria-label="Rotate Image"
                        tabIndex={0}
                        role="button"
                        className={cx('Icon-Rotate-Container', {
                            'Icon-Rotate-Container-disabled': isListingPhotoProcessing,
                        })}
                        size="sm"
                        fontColor={isListingPhotoProcessing ? 'gray300' : 'brand'}
                        onClick={this.handleRotateClick}
                        onKeyDown={this.keyDownHandler(this.handleRotateClick)}
                    />
                </div>
            </div>
        );
    }

    render() {
        const {
            currentSlideIndex,
            isOpen,
            isListingPhotoProcessing,
            list,
            isRotating,
            startSlide,
        } = this.state;

        const { className } = this.props;

        const header = `${currentSlideIndex + 1} of ${list.length}`;
        const parentSelector = ModalConstants.MODAL_PARENT_SELECTOR;

        return (
            <StyledImageCarouselModal
                className={className}
                scrollDocument={true}
                mediaQuery={{ xs: { fullScreen: true }, lg: { fullScreen: false } }}
                wide={true}
                isOpen={isOpen}
                header={(
                    <Heading level={6}>
                        {header}
                    </Heading>
                )}
                id={parentSelector}
                onClose={() => this.modalClose(false, true)}
            >
                <DialogBody>
                    <ImageCarousel
                        list={list}
                        currentSlideIndex={currentSlideIndex}
                        isListingPhotoProcessing={isListingPhotoProcessing}
                        isRotating={isRotating}
                        startSlide={startSlide}
                        // actions
                        updateCurrentSlideIndex={this.updateCurrentSlideIndex}
                    />
                    {this.renderForm()}
                </DialogBody>
            </StyledImageCarouselModal>
        );
    }
}

export { ImageCarouselModal };
export default connect()(ImageCarouselModal);
